import { Box, DataTable, DataTableProps, SortType, Spinner, Text } from 'grommet'
import { Search } from 'grommet-icons'
import { ReactNode, useState } from 'react'
import { FaCaretRight } from 'react-icons/fa'
import { Input } from '../Input'
import { Tab2, TabItem } from '../Tabs'

interface TableProps extends DataTableProps {
  data: any[]
  options?: TabItem[]
  dropDown?: ReactNode
  dataIsLoading?: boolean
  placeholder?: ReactNode
  searchVal?: string
  setOption?: (arg: string) => void
  activeOption?: string
  pagination?: ReactNode
  filter?: ReactNode
  iconLabel?: ReactNode
  searchPlaceholder?: string
  handleSearch?: (arg: string) => void
  header?: ReactNode
  isCollapsible?: boolean
  tableHeading?: string
  className?: string
}

export function Table({
  data,
  dataIsLoading,
  dropDown,
  filter,
  header,
  handleSearch,
  activeOption,
  placeholder,
  options,
  pagination,
  searchPlaceholder,
  searchVal,
  setOption,
  isCollapsible,
  tableHeading = 'Title',
  iconLabel,
  className,
  ...rest
}: TableProps) {
  const [sort, setSort] = useState<SortType>({
    direction: 'desc',
    property: 'name'
  })
  const [search, setSearch] = useState(false)
  const [collapsed, setCollapsed] = useState<boolean>(true)

  return (
    <Box
      border={{ size: 'xsmall' }}
      className={className}
      flex
      pad={!isCollapsible ? 'small' : ''}
      responsive
      round="15px"
    >
      {(options || dropDown || handleSearch || filter || iconLabel) && (
        <Box
          align="stretch"
          className="flex-col gap-3 md:flex-row md:items-center md:justify-between"
          direction="row"
          flex={false}
          gap="20px"
          justify="between"
          margin={{ bottom: (handleSearch && typeof searchVal === 'string') || filter ? '20px' : '20px' }}
          wrap
        >
          {options && (
            <Tab2
              activeTab={activeOption || ''}
              setActiveTab={arg => setOption && setOption(arg.toLowerCase())}
              tabs={options}
            />
          )}
          {dropDown && dropDown}
          {((handleSearch && typeof searchVal === 'string') || filter || iconLabel) && (
            <Box
              align="center"
              className="ml-0 w-full flex-wrap md:ml-auto md:w-auto"
              direction="row"
              gap="12px"
              justify="between"
            >
              <Box
                align="center"
                className="w-full min-w-0 flex-1 md:w-auto"
                direction="row"
                gap={iconLabel ? '20px' : '12px'}
              >
                {filter && filter}
                {handleSearch && typeof searchVal === 'string' && (
                  <Box className="w-full max-w-full md:w-[260px]" width="260px">
                    <Input
                      handleChange={e => handleSearch(e.target?.value)}
                      icon={<Search color="black" size="small" />}
                      onBlur={() => setSearch(false)}
                      onFocus={() => setSearch(true)}
                      placeholder={searchPlaceholder || 'Search....'}
                      style={{ backgroundColor: 'white', borderColor: search ? '#A79A46' : '#999999' }}
                      value={searchVal}
                    />
                  </Box>
                )}
              </Box>
              {iconLabel && iconLabel}
            </Box>
          )}
        </Box>
      )}
      <div
        className="overflow-auto"
        style={{
          transition: '0.35s ease-in-out'
        }}
      >
        {isCollapsible && (
          <Box
            align="center"
            background={collapsed ? '#F5F5F5' : '#CED8FF'}
            direction="row"
            justify="between"
            onClick={() => setCollapsed(!collapsed)}
            pad={'15px'}
            style={{
              transition: '0.35s ease-in-out'
            }}
          >
            <Box align="center" className="flex justify-between w-full" direction="row-reverse" gap="small">
              <div
                className={`cursor-pointer transition-transform ${collapsed ? 'rotate-0' : 'rotate-90'}`}
                style={{
                  transition: '0.35s ease-in-out'
                }}
              >
                <FaCaretRight size={18} />
              </div>
              <Text size="16px" weight={400}>
                {tableHeading}
              </Text>
            </Box>
          </Box>
        )}
        <div
          style={{
            maxHeight: collapsed && isCollapsible ? 0 : '10000px',
            overflow: 'hidden',
            transition: 'max-height 0.35s ease-in-out'
          }}
        >
          {header}
          <DataTable
            background={{ header: { color: '#F5F5F5' } }}
            border={{ body: { color: '#999999', side: 'bottom' }, header: { color: '#F5F5F5' } }}
            className="!border-collapse"
            data={data}
            onSort={setSort}
            pad={'14px'}
            paginate={true}
            placeholder={
              placeholder ||
              (dataIsLoading && (
                <Box align="center" justify="center" style={{ minHeight: '250px' }} width={'100%'}>
                  <Spinner height={'80px'} size="5px" width={'80px'} />
                  <Text weight="bold">Loading ...</Text>
                </Box>
              )) ||
              (!dataIsLoading && data?.length < 1 && (
                <div className="flex flex-col items-center justify-center my-12">
                  <h2 className="font-semibold text-2xl">No data found..</h2>
                  <img alt="not found" className="w-40 h-40" src="/not_found.png" />
                </div>
              ))
            }
            sort={sort}
            sortable={true}
            {...rest}
          />
        </div>
        {!dataIsLoading && data?.length > 0 && pagination && <Box margin={{ top: '20px' }}>{pagination}</Box>}
      </div>
    </Box>
  )
}

export * from './Table2'
